import React, { useEffect, useState } from "react"
import { ColumnDef } from "@tanstack/react-table"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import useDataTable from "hooks/useDataTable"
import DataTableTemplate from "Components/templates/data-table-template"
import ActionButton from "Components/atoms/buttons/action-button"
import Chip from "Components/atoms/chip"
import { SyncIcon } from "Components/atoms/icons"
import InfoTooltip from "Components/molecule/info-tooltip"
import withRouter from "Components/Common/withRouter"
import { MemoListItem } from "models/memo"
import { AdminMemosSliceState, setMemos } from "slices/admin/memos/reducer"
import { getMemos } from "slices/admin/memos/thunk"
import { navigateToMemoDetails } from "utils/navigator"
import { formatDateTimeAmPm } from "utils/tools"

const selectMemos = createSelector(
    (state: any) => state.AdminMemos,
    ({ result }: AdminMemosSliceState) => ({ result })
)

const periodOf = (n: MemoListItem) =>
    n.periodStart && n.periodEnd ? `${n.periodStart} → ${n.periodEnd}` : (n.periodEnd ?? "")

// Admin > System Updates Memo: notes written by the /memos skill (api/memos/*.md), reviewed and emailed from here.
const MemosPage = (props: any) => {
    document.title = "System Updates Memo | IbexIQ";
    const dispatch: any = useDispatch()
    const { result } = useSelector(selectMemos)

    const [isLoading, setIsLoading] = useState<boolean>(!result)
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false)

    const load = async () => {
        const res = await getMemos()
        if (res) dispatch(setMemos(res))
    }

    useEffect(() => {
        const init = async () => {
            if (!result) await load()
            setIsLoading(false)
        }
        init()
    }, [])

    const refresh = async () => {
        setIsRefreshing(true)
        await load()
        setIsRefreshing(false)
    }

    const openNote = (slug: string) => navigateToMemoDetails(props.router.navigate, slug)

    const columns = React.useMemo<ColumnDef<MemoListItem>[]>(() => [
        {
            header: "Title",
            accessorKey: "title",
            size: 320,
            cell: ({ row }) => <a href="#" onClick={(e) => { e.preventDefault(); openNote(row.original.slug) }}>
                {row.original.title}
            </a>
        },
        {
            header: "Period",
            id: "period",
            accessorFn: periodOf,
            size: 200,
        },
        {
            header: "Screens",
            id: "screens",
            accessorFn: (row) => row.screens.map(s => s.name).join(", "),
            size: 280,
        },
        {
            header: "PRs",
            id: "prs",
            accessorFn: (row) => row.prs.length,
            size: 70,
        },
        {
            header: "Status",
            id: "status",
            accessorFn: (row) => row.publishedAt ? "Sent" : "Draft",
            size: 100,
            cell: ({ row }) => row.original.publishedAt
                ? <Chip variant="success">Sent</Chip>
                : <Chip variant="secondary">Draft</Chip>
        },
        {
            header: "Last sent",
            id: "lastSent",
            accessorFn: (row) => row.sends.find(s => !s.testOnly)?.sentAt ?? "",
            size: 220,
            cell: ({ row }) => {
                const last = row.original.sends.find(s => !s.testOnly)
                return last ? `${formatDateTimeAmPm(last.sentAt)} · ${last.recipientCount} people` : "-"
            }
        },
    ], [])

    const table = useDataTable({
        columns,
        data: result?.notes ?? [],
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    return <DataTableTemplate
        title="Admin"
        breadcrumbItem="System Updates Memo"
        table={table}
        columns={columns}
        isLoading={isLoading}
        actions={[
            <InfoTooltip key="info" placement="bottom">
                Memos are written by the IbexIQ team from the latest GitHub changes. Open one to preview it, send yourself a test, then send it to every active employee.
            </InfoTooltip>,
            <ActionButton key="refresh" onClick={refresh} disabled={isRefreshing}>
                {isRefreshing ? "Refreshing..." : "Refresh"} <SyncIcon />
            </ActionButton>
        ]}
    />
}

export default withRouter(MemosPage)
